// Follow-up questions under an answer (answer-surface spec, §2).
//
// The limits live here because three places enforce them: the backend tool that
// accepts the model's questions, the store that keeps them, and the frontend
// that reads them back. One number in three files is how one of them drifts.
//
// A FOLLOW-UP IS UNTRUSTED TEXT. A model wrote it right after reading the site's
// rows, and those rows include free text anybody on the site can write. It is
// always rendered as plain text, short, and visible before it is pressed — and
// pressing it SENDS it, an exception to the morning brief's C4.4 ruling that
// holds only while every tool reads (answer-surface spec, §2 "Why sent"). The
// day a tool can write, that exception is re-decided, not inherited.

/**
 * At most this many chips under one answer. Three, one of each kind rule 13
 * asks for: a closer look at what needs attention in this answer, the natural
 * next question, and one that crosses into another source. Two left no room
 * for the cross-source one, which is the question a reader is least likely to
 * think of typing.
 */
export const MAX_FOLLOW_UPS = 3;

/** One question's length, in characters, after normalisation. */
export const MAX_FOLLOW_UP_CHARS = 80;

/**
 * The one normal form a follow-up is compared and stored in: NFKC (full-width
 * `３F` is `3F`), every invisible character gone, every run of whitespace —
 * newlines included — one space, ends trimmed. The backend's "was a chip
 * taken?" check compares in this form, so a question typed with a stray space
 * still counts.
 *
 * INVISIBLE CHARACTERS ARE REMOVED, not kept, because a chip SENDS on a click
 * and must send exactly what the reader saw. Format characters (`\p{Cf}`) —
 * zero-width spaces and joiners, bidi overrides and isolates, the BOM, the tag
 * characters — render as nothing or reorder what does render, so a chip could
 * read one question and send another. Then the controls (`\p{Cc}`), except the
 * ones that are line or tab breaks, which become the space they look like.
 * Format characters go first because JavaScript's `\s` counts the BOM as
 * whitespace, and a BOM is not a space anybody saw.
 */
export function normalizeFollowUp(text: string): string {
  return text
    .normalize('NFKC')
    .replace(/\p{Cf}/gu, '')
    .replace(/[\t\n\v\f\r\u0085]/g, ' ')
    .replace(/\p{Cc}/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Stored or streamed follow-ups, read leniently: non-strings, empties, over-long
 * entries and repeats are dropped, and at most `MAX_FOLLOW_UPS` survive.
 */
export function readFollowUps(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const item of raw as unknown[]) {
    if (typeof item !== 'string') continue;
    const question = normalizeFollowUp(item);
    if (question === '' || question.length > MAX_FOLLOW_UP_CHARS || out.includes(question)) continue;
    out.push(question);
    if (out.length === MAX_FOLLOW_UPS) break;
  }
  return out;
}
